
import { supabase } from "@/integrations/supabase/client";
import { Plan } from "@/components/landing/pricing/pricingData";
import { useToast } from "@/hooks/use-toast";

export interface PaymentData {
  amount: number;
  currency: string;
  email: string;
  name?: string;
  planName: string;
  planId: number; // Add plan ID for proper mapping
  redirectUrl: string;
  isAnnual: boolean;
}

export const usePaymentService = () => {
  const { toast } = useToast();

  const initiateFlutterwavePayment = async (
    plan: Plan,
    isAnnual: boolean,
    redirectUrl: string
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to subscribe to a plan",
          variant: "destructive",
        });
        return null;
      }

      // Get user email
      const userEmail = user.email;
      if (!userEmail) {
        toast({
          title: "Error",
          description: "Could not retrieve user email",
          variant: "destructive",
        });
        return null;
      }

      // Map plans to plan IDs - these are the unique identifiers for each plan
      const planIdMap: Record<string, number> = {
        "Essential": isAnnual ? 138704 : 138701, // Different IDs for monthly and annual
        "Growth": isAnnual ? 138705 : 138703,
        "Pro": isAnnual ? 138706 : 138702,
      };

      const planId = planIdMap[plan.name];
      if (!planId) {
        toast({
          title: "Error",
          description: "Invalid plan selected",
          variant: "destructive",
        });
        return null;
      }

      console.log("Initiating payment for plan:", plan.name, "planId:", planId, "isAnnual:", isAnnual);

      // Prepare payment data with plan ID included
      const paymentData: PaymentData = {
        amount: isAnnual ? plan.yearlyPrice : plan.monthlyPrice,
        currency: "USD",
        email: userEmail,
        name: user.user_metadata?.name || userEmail,
        planName: plan.name,
        planId, // Include the plan ID
        redirectUrl,
        isAnnual,
      };

      // Call payment processing endpoint
      const { data, error } = await supabase.functions.invoke("process-payment", {
        body: {
          paymentData,
          userId: user.id,
          planId,
        },
      });

      if (error) {
        console.error("Payment function invocation error:", error);
        toast({
          title: "Payment Error",
          description: "Failed to initiate payment. Please try again.",
          variant: "destructive",
        });
        return null;
      }

      if (!data || !data.success) {
        console.error("Payment initiation failed:", data?.error || "Unknown error");
        toast({
          title: "Payment Error",
          description: data?.error || "Failed to initiate payment. Please try again.",
          variant: "destructive",
        });
        return null;
      }

      return {
        paymentLink: data.paymentLink,
        transactionRef: data.paymentRef,
      };
    } catch (error) {
      console.error("Payment service error:", error);
      toast({
        title: "Payment Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      return null;
    }
  };

  const verifyPayment = async (transactionRef: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("verify-payment", {
        body: { transactionRef },
      });

      if (error) {
        console.error("Payment verification failed:", error);
        toast({
          title: "Verification Error",
          description: "Failed to verify payment status",
          variant: "destructive",
        });
        return false;
      }

      if (data.success && data.status === "completed") {
        toast({
          title: "Payment Successful",
          description: "Your subscription has been activated",
        });
        return true;
      } else {
        toast({
          title: "Payment Pending",
          description: "Your payment is being processed",
        });
        return false;
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      toast({
        title: "Verification Error",
        description: "An unexpected error occurred while verifying payment",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    initiateFlutterwavePayment,
    verifyPayment,
  };
};
